import { useMemo } from 'react';
import { useGetLeadsFilterOptionsQuery } from '@api/apiCompat';
import { useCustomFiltersQueryParam } from '@hooks/common/useDynamicFilters/useCustomFiltersQueryParam';

import { createJobsFilters, createPropertyFilters, createScrapekingDbFilters } from '@/shared/SearchFilters';
import AutocompleteSearchFilter from '@/shared/SearchFilters/AutocompleteSearchFilter';

// Lead type options for the unified ScrapeKing DB
const LEAD_TYPE_OPTIONS = [
  { label: 'Local Business', value: 'local_search' },
  { label: 'Professional / LinkedIn', value: 'social_media' },
  { label: 'Real Estate', value: 'real_estate' },
  { label: 'Jobs', value: 'jobs' },
];

// Map lead_type filter values to backend campaignType IDs for filter-options API
const leadTypeToApiId: Record<string, number | undefined> = {
  local_search: 1,
  real_estate: 3,
  jobs: 4,
  social_media: 5,
};

// Type for filter options from API
interface FilterOption {
  value: string;
  label: string;
  count?: number;
}

interface FilterOptionsResponse {
  sources?: FilterOption[];
  categories?: FilterOption[];
  positions?: FilterOption[];
  industries?: FilterOption[];
  ratings?: FilterOption[];
  states?: FilterOption[];
  employee_ranges?: FilterOption[];
  stats?: {
    total?: number;
    has_phone?: number;
    has_email?: number;
    has_website?: number;
    has_linkedin?: number;
    has_additional_contacts?: number;
    has_rating?: number;
    has_personal_email?: number;
    has_headline?: number;
    has_employee_count?: number;
  };
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const useDynamicFilters = (campaignType?: string) => {
  // Read current lead_type from URL filters to determine which filter set to show
  const { dynamicParam } = useCustomFiltersQueryParam();
  let currentLeadType = '';
  if (dynamicParam) {
    try {
      const parsed = JSON.parse(dynamicParam);
      const lt = parsed?.lead_type;
      // AutocompleteSearchFilter stores [{label, value}] arrays
      if (Array.isArray(lt) && lt.length > 0) {
        currentLeadType = lt[0]?.value || '';
      } else if (typeof lt === 'string') {
        currentLeadType = lt;
      }
    } catch {
      // ignore
    }
  }

  // Resolve API campaign type ID from lead_type filter
  const apiCampaignTypeId = currentLeadType ? leadTypeToApiId[currentLeadType] : undefined;

  // Fetch filter options from API
  const { data: rawFilterOptions } = useGetLeadsFilterOptionsQuery(
    apiCampaignTypeId ? { campaignType: apiCampaignTypeId } : ({} as any),
  );
  const filterOptions = rawFilterOptions as FilterOptionsResponse | undefined;

  const filters = useMemo(() => {
    // Lead Type selector is always the first filter
    const leadTypeFilter = {
      field: 'lead_type',
      Component: AutocompleteSearchFilter,
      label: 'Lead Type',
      options: LEAD_TYPE_OPTIONS,
    };

    // Get type-specific filters based on current selection
    let typeFilters: any[];
    if (currentLeadType === 'real_estate') {
      typeFilters = createPropertyFilters();
    } else if (currentLeadType === 'jobs') {
      typeFilters = createJobsFilters();
    } else {
      // Default: ScrapekingDb filters (works for all, local_search, social_media)
      typeFilters = createScrapekingDbFilters(filterOptions);
    }

    return [leadTypeFilter, ...typeFilters];
  }, [currentLeadType, filterOptions]);

  return { filters };
};
